import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/config/routes';
import { useOnboardingSession } from '@/contexts/OnboardingContext';
import { PersonDetailsForm } from '../components/PersonDetailsForm';
import type { Step2Data, PersonExtendedDetails } from '@/types/onboarding';

/**
 * Onboarding Wizard - Step 2: Parents Full Details
 *
 * This step collects comprehensive information about both parents:
 * - Father: firstName, lastName (required), plus optional birth/death details
 * - Mother: firstName, lastName (required), plus optional birth/death details
 *
 * Features:
 * - Pre-populates from session.draft_data.step2 if available
 * - Real-time validation for required fields
 * - Auto-save on successful validation
 * - Back navigation to Step 1
 * - Forward navigation to Step 3 (after save)
 * - Loading states during save
 * - Error handling with user-friendly messages
 *
 * Design follows Stitch HTML reference:
 * @see E:\Job\IT\50Studios\Fiels\Clickup_Projects\projects\family-tree\files\stitch\stitch\wizard_parents_refined\code.html
 *
 * Integration:
 * - Uses OnboardingContext for session state and saveStep function
 * - Uses PersonDetailsForm (reusable component)
 * - Matches existing WelcomeStep patterns
 */
export function Step2() {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const {
    session,
    isLoadingSession,
    isSaving,
    saveError,
    saveStep,
  } = useOnboardingSession();

  // Form state - separate for father and mother
  const [fatherData, setFatherData] = useState<PersonExtendedDetails>({
    firstName: '',
    lastName: '',
    gender: 'male',
  });

  const [motherData, setMotherData] = useState<PersonExtendedDetails>({
    firstName: '',
    lastName: '',
    gender: 'female',
  });

  // Validation state
  const [showErrors, setShowErrors] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    father?: Partial<Record<keyof PersonExtendedDetails, string>>;
    mother?: Partial<Record<keyof PersonExtendedDetails, string>>;
  }>({});

  // Pre-populate from session data
  useEffect(() => {
    if (session?.draft_data?.step2) {
      const { father, mother } = session.draft_data.step2;

      if (father) {
        setFatherData({
          ...father,
          gender: 'male', // Ensure gender is set
        });
      }

      if (mother) {
        setMotherData({
          ...mother,
          gender: 'female', // Ensure gender is set
        });
      }
    }
  }, [session]);

  // Validation function
  const validateForm = useCallback((): boolean => {
    const errors: typeof validationErrors = {};

    // Validate father
    if (!fatherData.firstName.trim()) {
      errors.father = {
        ...errors.father,
        firstName: t('step2.errors.firstNameRequired', 'First name is required'),
      };
    }

    if (!fatherData.lastName.trim()) {
      errors.father = {
        ...errors.father,
        lastName: t('step2.errors.lastNameRequired', 'Family name is required'),
      };
    }

    // Validate mother
    if (!motherData.firstName.trim()) {
      errors.mother = {
        ...errors.mother,
        firstName: t('step2.errors.firstNameRequired', 'First name is required'),
      };
    }

    if (!motherData.lastName.trim()) {
      errors.mother = {
        ...errors.mother,
        lastName: t('step2.errors.lastNameRequired', 'Family name is required'),
      };
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [fatherData, motherData, t]);

  // Auto-validate when data changes (but don't show errors until user tries to submit)
  useEffect(() => {
    if (showErrors) {
      validateForm();
    }
  }, [fatherData, motherData, showErrors, validateForm]);

  // Handle back navigation
  const handleBack = () => {
    navigate(ROUTES.ONBOARDING);
  };

  // Handle next/save
  const handleNext = async () => {
    setShowErrors(true);

    if (!validateForm()) {
      toast.error(t('step2.errors.validationFailed', 'Please fill in all required fields'));
      return;
    }

    try {
      // Prepare Step 2 data
      const step2Data: Step2Data = {
        father: {
          ...fatherData,
          gender: 'male',
        },
        mother: {
          ...motherData,
          gender: 'female',
        },
      };

      // Save to backend
      await saveStep(2, step2Data);

      // Success - navigate to Step 3
      toast.success(t('step2.saveSuccess', 'Parents information saved successfully'));
      navigate(ROUTES.ONBOARDING_STEP_3);
    } catch (error) {
      console.error('Failed to save Step 2:', error);

      // Show error toast
      toast.error(
        saveError?.message || t('step2.errors.saveFailed', 'Failed to save progress. Please try again.')
      );
    }
  };

  // Handle skip remaining steps - go directly to success
  const handleSkipRemaining = async () => {
    setShowErrors(true);

    if (!validateForm()) {
      toast.error(t('step2.errors.validationFailed', 'Please fill in all required fields before skipping'));
      return;
    }

    try {
      // Save Step 2 data first
      const step2Data: Step2Data = {
        father: {
          ...fatherData,
          gender: 'male',
        },
        mother: {
          ...motherData,
          gender: 'female',
        },
      };
      await saveStep(2, step2Data);

      // Mark Step 3 and 4 as skipped
      await saveStep(3, { skipped: true });
      await saveStep(4, { skipped: true });

      // Success - navigate directly to success screen
      toast.success(t('step2.skipSuccess', 'Parents saved. Skipping grandparents steps.'));
      navigate(ROUTES.ONBOARDING_SUCCESS);
    } catch (error) {
      console.error('Failed to skip remaining steps:', error);

      // Show error toast
      toast.error(
        saveError?.message || t('step2.errors.skipFailed', 'Failed to skip. Please try again.')
      );
    }
  };

  // Show loading state while session is loading
  if (isLoadingSession) {
    return (
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">{t('common.loading', 'Loading...')}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 flex justify-center py-6 sm:py-8 md:py-10 px-4 sm:px-6 md:px-8">
      <div className="layout-content-container flex flex-col max-w-5xl flex-1 gap-5 sm:gap-6 md:gap-8">
        {/* Progress Section */}
        <div className="flex flex-col gap-3 sm:gap-4">
          <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
            <div className="flex flex-col">
              <span className="text-primary text-xs sm:text-sm font-bold uppercase tracking-wider">
                {t('step2.progress', 'Step 2 of 4')}
              </span>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mt-0.5 sm:mt-1 text-slate-900 dark:text-slate-100">
                {t('step2.title', 'Tell us about your parents')}
              </h1>
            </div>
            <span className="text-slate-500 dark:text-slate-400 font-medium bg-slate-100 dark:bg-slate-800 px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm flex-shrink-0">
              {t('step2.completionBadge', '1/4 Complete')}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-slate-200 dark:bg-slate-700 h-2 sm:h-2.5 rounded-full overflow-hidden">
            <div
              className="bg-primary h-full transition-all duration-500"
              style={{ width: '25%' }}
            />
          </div>
        </div>

        {/* Form Section - Two Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6 lg:gap-8">
          {/* Father's Form */}
          <PersonDetailsForm
            title={t('step2.father', 'Father')}
            icon="man"
            initialData={fatherData}
            onChange={setFatherData}
            errors={validationErrors.father}
            showErrors={showErrors}
          />

          {/* Mother's Form */}
          <PersonDetailsForm
            title={t('step2.mother', 'Mother')}
            icon="woman"
            initialData={motherData}
            onChange={setMotherData}
            errors={validationErrors.mother}
            showErrors={showErrors}
          />
        </div>

        {/* Footer Navigation */}
        <div className="flex flex-col gap-3 pt-4 sm:pt-5 md:pt-6 mt-2 sm:mt-3 md:mt-4 border-t border-slate-200 dark:border-slate-800">
          {/* Primary Navigation Row */}
          <div className="flex items-center justify-between gap-3">
            {/* Back Button */}
            <Button
              onClick={handleBack}
              variant="ghost"
              size="lg"
              disabled={isSaving}
              className="px-4 sm:px-6 md:px-8 h-11 sm:h-12 md:h-14 flex items-center justify-center gap-1.5 sm:gap-2 text-slate-600 dark:text-slate-400 font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-sm sm:text-base"
            >
              <span className="material-symbols-outlined text-lg sm:text-xl">arrow_back</span>
              <span className="hidden sm:inline">{t('step2.back', 'Back')}</span>
            </Button>

            {/* Next Button */}
            <Button
              onClick={handleNext}
              size="lg"
              disabled={isSaving}
              className="px-6 sm:px-8 md:px-10 h-11 sm:h-12 md:h-14 bg-primary text-white rounded-lg sm:rounded-xl font-bold shadow-lg shadow-primary/20 hover:brightness-110 active:scale-[0.98] transition-all text-sm sm:text-base"
            >
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span className="hidden sm:inline">{t('step2.saving', 'Saving...')}</span>
                  <span className="sm:hidden">{t('step2.savingShort', 'Saving')}</span>
                </span>
              ) : (
                <>
                  <span className="hidden sm:inline">{t('step2.continue', 'Continue to Step 3')}</span>
                  <span className="sm:hidden">{t('step2.continueShort', 'Continue')}</span>
                </>
              )}
            </Button>
          </div>

          {/* Skip Remaining Steps - Secondary Action */}
          <div className="flex justify-center pt-1">
            <Button
              onClick={handleSkipRemaining}
              variant="ghost"
              size="sm"
              disabled={isSaving}
              className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 underline-offset-4 hover:underline"
            >
              {t('step2.skipRemaining', 'Skip remaining steps')}
            </Button>
          </div>
        </div>
      </div>
    </main>
  );
}
